import { useEffect, useRef } from "react";

const FALLBACK_REFRESH_MS = 60_000;
const FALLBACK_JITTER_MS = 10_000;
const REVISION_COALESCE_MS = 50;
const RETRY_DELAYS_MS = [250, 500, 1_000, 2_000, 5_000] as const;

interface CourtStateEventPayload {
  facility_id: string;
  revision: number;
}

type Reconcile = (
  revision: number | undefined,
) => void | boolean | Promise<void | boolean>;

interface RevisionSubscriber {
  active: boolean;
  isAuthorized: () => boolean;
  verifiedRevision: number;
  reconcile: Reconcile;
  onRevisionPending: ((revision: number) => void) | undefined;
  pendingRevision: number | undefined;
  pendingFallback: boolean;
  inFlight: boolean;
  retryAttempt: number;
  minimumReconcileIntervalMs: number;
  lastReconcileStartedAt: number;
  coalesceTimer: number | undefined;
  retryTimer: number | undefined;
}

interface CourtStateChannel {
  source: EventSource;
  facilityId: string;
  path: (facilityId: string, revision: number) => string;
  transportCursor: number;
  highestSeenEvent: number;
  subscribers: Set<RevisionSubscriber>;
  eventName: string;
  onCourtState: (event: Event) => void;
  lastReconnectAt: number;
}

interface CourtStateSubscriptionHandle {
  updateVerifiedRevision: (revision: number) => void;
  requestFallback: () => void;
  reconnect: () => void;
  unsubscribe: () => void;
}

const courtStateChannels = new Map<string, CourtStateChannel>();

export interface UseCourtStateStreamOptions {
  facilityId: string;
  revision: number;
  onReconcile: Reconcile;
  onRevisionPending?: (revision: number) => void;
  enabled?: boolean;
  isAuthorized?: () => boolean;
}

export interface UseFacilityRevisionStreamOptions
  extends UseCourtStateStreamOptions {
  channelName: string;
  eventName: string;
  path: (facilityId: string, revision: number) => string;
  minimumReconcileIntervalMs?: number;
}

function parseCourtStateEvent(
  event: MessageEvent<string>,
  facilityId: string,
): CourtStateEventPayload | null {
  try {
    const payload = JSON.parse(event.data) as Partial<CourtStateEventPayload>;
    if (
      payload.facility_id !== facilityId ||
      !Number.isSafeInteger(payload.revision) ||
      (payload.revision ?? -1) < 0
    ) {
      return null;
    }
    return payload as CourtStateEventPayload;
  } catch {
    return null;
  }
}

function retryDelay(attempt: number): number {
  return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
}

function clearSubscriberTimers(subscriber: RevisionSubscriber): void {
  if (subscriber.coalesceTimer !== undefined) {
    window.clearTimeout(subscriber.coalesceTimer);
    subscriber.coalesceTimer = undefined;
  }
  if (subscriber.retryTimer !== undefined) {
    window.clearTimeout(subscriber.retryTimer);
    subscriber.retryTimer = undefined;
  }
}

function reconnectChannel(channel: CourtStateChannel): void {
  const now = performance.now();
  if (now - channel.lastReconnectAt < REVISION_COALESCE_MS) return;

  channel.source.removeEventListener(channel.eventName, channel.onCourtState);
  channel.source.close();
  const cursor = Math.max(channel.transportCursor, channel.highestSeenEvent);
  channel.transportCursor = cursor;
  channel.lastReconnectAt = now;
  channel.source = new EventSource(channel.path(channel.facilityId, cursor));
  channel.source.addEventListener(channel.eventName, channel.onCourtState);
}

function scheduleSubscriber(subscriber: RevisionSubscriber): void {
  if (
    !subscriber.active ||
    !subscriber.isAuthorized() ||
    subscriber.inFlight ||
    subscriber.coalesceTimer !== undefined ||
    subscriber.retryTimer !== undefined
  ) {
    return;
  }
  const sinceLastRun = performance.now() - subscriber.lastReconcileStartedAt;
  const minimumDelay = Math.max(
    0,
    subscriber.minimumReconcileIntervalMs - sinceLastRun,
  );
  const delay = Math.max(REVISION_COALESCE_MS, minimumDelay);
  subscriber.coalesceTimer = window.setTimeout(() => {
    subscriber.coalesceTimer = undefined;
    void runSubscriber(subscriber);
  }, delay);
}

function requestReconcile(
  subscriber: RevisionSubscriber,
  revision: number | undefined,
): void {
  if (!subscriber.active || !subscriber.isAuthorized()) return;
  if (revision === undefined) {
    subscriber.pendingFallback = true;
  } else {
    if (revision <= subscriber.verifiedRevision) return;
    subscriber.onRevisionPending?.(revision);
    subscriber.pendingRevision = Math.max(
      subscriber.pendingRevision ?? -1,
      revision,
    );
  }
  if (subscriber.retryTimer !== undefined) {
    window.clearTimeout(subscriber.retryTimer);
    subscriber.retryTimer = undefined;
  }
  scheduleSubscriber(subscriber);
}

async function runSubscriber(subscriber: RevisionSubscriber): Promise<void> {
  if (!subscriber.active || !subscriber.isAuthorized() || subscriber.inFlight) {
    return;
  }
  const targetRevision = subscriber.pendingRevision;
  const includesFallback = subscriber.pendingFallback;
  if (targetRevision === undefined && !includesFallback) return;

  subscriber.pendingRevision = undefined;
  subscriber.pendingFallback = false;
  subscriber.inFlight = true;
  subscriber.lastReconcileStartedAt = performance.now();
  let failed = false;
  try {
    failed = (await subscriber.reconcile(targetRevision)) === false;
  } catch {
    failed = true;
  }

  if (failed) {
    if (
      targetRevision !== undefined &&
      targetRevision > subscriber.verifiedRevision
    ) {
      subscriber.pendingRevision = Math.max(
        subscriber.pendingRevision ?? -1,
        targetRevision,
      );
    }
    subscriber.pendingFallback ||= includesFallback;
  } else {
    if (targetRevision !== undefined) {
      subscriber.verifiedRevision = Math.max(
        subscriber.verifiedRevision,
        targetRevision,
      );
    }
    subscriber.retryAttempt = 0;
  }
  subscriber.inFlight = false;

  if (failed) {
    if (!subscriber.active || !subscriber.isAuthorized()) return;
    const delay = retryDelay(subscriber.retryAttempt);
    subscriber.retryAttempt += 1;
    subscriber.retryTimer = window.setTimeout(() => {
      subscriber.retryTimer = undefined;
      scheduleSubscriber(subscriber);
    }, delay);
    return;
  }
  scheduleSubscriber(subscriber);
}

function subscribeToCourtState(
  channelName: string,
  facilityId: string,
  revision: number,
  reconcile: Reconcile,
  onRevisionPending: ((revision: number) => void) | undefined,
  eventName: string,
  path: (facilityId: string, revision: number) => string,
  minimumReconcileIntervalMs: number,
  isAuthorized: () => boolean,
): CourtStateSubscriptionHandle {
  const channelKey = `${channelName}:${facilityId}`;
  let channel = courtStateChannels.get(channelKey);
  if (channel === undefined) {
    const source = new EventSource(path(facilityId, revision));
    channel = {
      source,
      facilityId,
      path,
      transportCursor: revision,
      highestSeenEvent: revision,
      subscribers: new Set(),
      eventName,
      onCourtState: () => undefined,
      lastReconnectAt: Number.NEGATIVE_INFINITY,
    };
    const activeChannel = channel;
    activeChannel.onCourtState = (rawEvent: Event): void => {
      const event = parseCourtStateEvent(
        rawEvent as MessageEvent<string>,
        facilityId,
      );
      if (event === null || event.revision <= activeChannel.highestSeenEvent) {
        return;
      }
      activeChannel.highestSeenEvent = event.revision;
      for (const subscriber of activeChannel.subscribers) {
        requestReconcile(subscriber, event.revision);
      }
    };
    source.addEventListener(eventName, activeChannel.onCourtState);
    courtStateChannels.set(channelKey, activeChannel);
  }

  const subscriber: RevisionSubscriber = {
    active: true,
    isAuthorized,
    verifiedRevision: revision,
    reconcile,
    onRevisionPending,
    pendingRevision: undefined,
    pendingFallback: false,
    inFlight: false,
    retryAttempt: 0,
    minimumReconcileIntervalMs,
    lastReconcileStartedAt: Number.NEGATIVE_INFINITY,
    coalesceTimer: undefined,
    retryTimer: undefined,
  };
  channel.subscribers.add(subscriber);

  const knownRevision = Math.max(
    channel.transportCursor,
    channel.highestSeenEvent,
    ...Array.from(
      channel.subscribers,
      (activeSubscriber) => activeSubscriber.verifiedRevision,
    ),
  );
  for (const activeSubscriber of channel.subscribers) {
    requestReconcile(activeSubscriber, knownRevision);
  }

  return {
    updateVerifiedRevision(nextRevision: number): void {
      subscriber.verifiedRevision = Math.max(
        subscriber.verifiedRevision,
        nextRevision,
      );
      if (
        subscriber.pendingRevision !== undefined &&
        subscriber.pendingRevision <= subscriber.verifiedRevision
      ) {
        subscriber.pendingRevision = undefined;
      }
    },
    requestFallback(): void {
      requestReconcile(subscriber, undefined);
    },
    reconnect(): void {
      if (!subscriber.active || !subscriber.isAuthorized()) return;
      const activeChannel = courtStateChannels.get(channelKey);
      if (activeChannel !== undefined) reconnectChannel(activeChannel);
    },
    unsubscribe(): void {
      subscriber.active = false;
      clearSubscriberTimers(subscriber);
      const activeChannel = courtStateChannels.get(channelKey);
      if (activeChannel === undefined) return;
      activeChannel.subscribers.delete(subscriber);
      if (activeChannel.subscribers.size > 0) return;
      activeChannel.source.removeEventListener(
        activeChannel.eventName,
        activeChannel.onCourtState,
      );
      activeChannel.source.close();
      courtStateChannels.delete(channelKey);
    },
  };
}

function fallbackDelay(): number {
  return FALLBACK_REFRESH_MS + Math.floor(Math.random() * FALLBACK_JITTER_MS);
}

/**
 * Subscribe to a facility's durable court-state cursor.
 *
 * Events are wake signals only. Each consumer tracks its own verified revision;
 * transport ordering never marks a failed REST reconciliation as applied.
 */
export function useFacilityRevisionStream({
  facilityId,
  revision,
  onReconcile,
  onRevisionPending,
  enabled = true,
  isAuthorized,
  channelName,
  eventName,
  path,
  minimumReconcileIntervalMs = 0,
}: UseFacilityRevisionStreamOptions): void {
  const reconcile = useRef(onReconcile);
  const authorization = useRef(isAuthorized);
  const currentRevision = useRef(revision);
  const subscription = useRef<CourtStateSubscriptionHandle | null>(null);
  currentRevision.current = revision;
  authorization.current = isAuthorized;

  useEffect(() => {
    reconcile.current = onReconcile;
  }, [onReconcile]);

  useEffect(() => {
    subscription.current?.updateVerifiedRevision(revision);
  }, [revision]);

  useEffect(() => {
    const isAuthorizedNow = (): boolean => authorization.current?.() ?? true;
    if (!enabled || !facilityId || !isAuthorizedNow()) return;

    const handle =
      typeof EventSource === "undefined"
        ? null
        : subscribeToCourtState(
            channelName,
            facilityId,
            currentRevision.current,
            (nextRevision) => reconcile.current(nextRevision),
            onRevisionPending,
            eventName,
            path,
            minimumReconcileIntervalMs,
            isAuthorizedNow,
          );
    subscription.current = handle;

    const refreshIfVisible = (): void => {
      if (document.visibilityState === "visible" && isAuthorizedNow()) {
        if (handle === null) {
          void Promise.resolve(reconcile.current(undefined)).catch(
            () => undefined,
          );
        } else {
          handle.requestFallback();
        }
      }
    };

    const resumeIfVisible = (): void => {
      if (document.visibilityState !== "visible" || !isAuthorizedNow()) return;
      handle?.reconnect();
      refreshIfVisible();
    };

    let fallbackTimer: number | undefined;
    const scheduleFallback = (): void => {
      fallbackTimer = window.setTimeout(() => {
        refreshIfVisible();
        scheduleFallback();
      }, fallbackDelay());
    };
    scheduleFallback();
    window.addEventListener("online", resumeIfVisible);
    document.addEventListener("visibilitychange", resumeIfVisible);

    return () => {
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      window.removeEventListener("online", resumeIfVisible);
      document.removeEventListener("visibilitychange", resumeIfVisible);
      handle?.unsubscribe();
      if (subscription.current === handle) subscription.current = null;
    };
  }, [
    channelName,
    enabled,
    eventName,
    facilityId,
    minimumReconcileIntervalMs,
    onRevisionPending,
    path,
  ]);
}

const courtStatePath = (facilityId: string, revision: number): string =>
  `/bff/api/facilities/${encodeURIComponent(facilityId)}` +
  "/drop-in/court-state/events" +
  `?after_revision=${revision}`;

export function useCourtStateStream(options: UseCourtStateStreamOptions): void {
  useFacilityRevisionStream({
    ...options,
    channelName: "court-state",
    eventName: "court-state",
    path: courtStatePath,
  });
}
